import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { required } from 'redux-form-validators';
import { FormattedMessage, injectIntl } from 'react-intl';
import { DefaultButton, ReduxSelectField } from 'common/components';
import { FormMessageHOC } from 'common';
import { createUserPartnerAccount as createUserPartnerAccountRequest } from '../../api';
import LinkDistributionSelector from '../../../partnership/_components/LinkDistributionSelector/LinkDistributionSelector';

import { staticStyles } from './style';
// 检查重复项的函数，仅在提交时使用
const checkDuplicateItems = (partnershipConditions, partnershipConditionsList) => {
  // 如果没有选择或只选择了一个条件，不需要校验
  if (!partnershipConditions || !Array.isArray(partnershipConditions) || partnershipConditions.length <= 1) {
    return undefined;
  }

  // 创建一个统一的映射来存储所有已使用的program值及其来源字段类型
  // key: program值, value: {字段类型, 条件索引}
  const allProgramsMap = new Map();
  const allMasterProgramsMap = new Map();

  // 遍历查找重复项
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < partnershipConditions.length; i++) {
    const conditionId = partnershipConditions[i];
    // 找到对应的条件详情
    const condition = partnershipConditionsList.find(cond => cond.id === conditionId);

    // eslint-disable-next-line no-continue
    if (!condition) continue;

    // 检查table_program是否与任何已存在的program重复
    if (condition.tableProgram) {
      if (allProgramsMap.has(condition.tableProgram)) {
        const existingProgram = allProgramsMap.get(condition.tableProgram);
        if (existingProgram.title !== condition.title) {
          return `Duplicate IB Programs:${existingProgram.title} & ${condition.title}`;
        }
      }
      allProgramsMap.set(condition.tableProgram, {
        fieldType: 'table_program',
        conditionIndex: i,
        title: condition.title,
      });
    }

    // 检查a_book_program是否与任何已存在的program重复
    if (condition.aBookProgram) {
      if (allProgramsMap.has(condition.aBookProgram)) {
        const existingProgram = allProgramsMap.get(condition.aBookProgram);
        if (existingProgram.title !== condition.title) {
          return `Duplicate IB Programs:${existingProgram.title} & ${condition.title}`;
        }
      }
      allProgramsMap.set(condition.aBookProgram, {
        fieldType: 'a_book_program',
        conditionIndex: i,
        title: condition.title,
      });
    }

    // 检查b_book_program是否与任何已存在的program重复
    if (condition.bBookProgram) {
      if (allProgramsMap.has(condition.bBookProgram)) {
        const existingProgram = allProgramsMap.get(condition.bBookProgram);
        if (existingProgram.title !== condition.title) {
          return `Duplicate IB Programs:${existingProgram.title} & ${condition.title}`;
        }
      }
      allProgramsMap.set(condition.bBookProgram, {
        fieldType: 'b_book_program',
        conditionIndex: i,
        title: condition.title,
      });
    }

    // 检查master_reward_table是否与任何已存在的program重复
    if (condition.masterRewardTableProgram) {
      if (allMasterProgramsMap.has(condition.masterRewardTableProgram)) {
        const existingProgram = allMasterProgramsMap.get(condition.masterRewardTableProgram);
        return `Duplicate Master IB Programs:${existingProgram.title} & ${condition.title}`;
      }
      allMasterProgramsMap.set(condition.masterRewardTableProgram, {
        fieldType: 'master_table_program',
        conditionIndex: i,
        title: condition.title,
      });
    }
    // 检查master_a_book_table是否与任何已存在的program重复
    if (condition.masterABookTableProgram) {
      if (allMasterProgramsMap.has(condition.masterABookTableProgram)) {
        const existingProgram = allMasterProgramsMap.get(condition.masterABookTableProgram);
        return `Duplicate Master IB Programs:${existingProgram.title} & ${condition.title}`;
      }
      allMasterProgramsMap.set(condition.masterABookTableProgram, {
        fieldType: 'master_a_book_program',
        conditionIndex: i,
        title: condition.title,
      });
    }
    // 检查master_b_book_table是否与任何已存在的program重复
    if (condition.masterBBookTableProgram) {
      if (allMasterProgramsMap.has(condition.masterBBookTableProgram)) {
        const existingProgram = allMasterProgramsMap.get(condition.masterBBookTableProgram);
        return `Duplicate Master IB Programs:${existingProgram.title} & ${condition.title}`;
      }
      allMasterProgramsMap.set(condition.masterBBookTableProgram, {
        fieldType: 'master_b_book_program',
        conditionIndex: i,
        title: condition.title,
      });
    }
  }

  return undefined; // 校验通过
};

const UserPartnerAccountCreateForm = ({
  userId,
  rewardFrequencies,
  partnershipConditionsList,
  handleSubmit,
  submitting,
  invalid,
  intl,
  error,
  getPartnershipConditionsList,
  resetKey,
  showFormSuccessMessage,
  showFormFailMessage,
  onRequestClose,
  reset,
  hasIbContractSignature,
}) => {
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [showContractWarning, setShowContractWarning] = useState(false);
  useEffect(() => {
    setSelectedCodes([]);
    setShowContractWarning(false);
    if (reset) reset();
    // resetKey changes only when the modal is opened for a new submission.
    // Do not depend on the redux-form reset callback: it can be recreated
    // during field updates and would clear newly selected templates.
  }, [resetKey]);
  const handleDistributionError = useCallback(() => showFormFailMessage('formFailMessageText'), [showFormFailMessage]);
  const submit = handleSubmit(values => {
    // 在提交时检查重复项
    const duplicateError = checkDuplicateItems(values.partnershipConditions, partnershipConditionsList);
    if (duplicateError) {
      // 显示错误提示
      alert(duplicateError);
      // 返回一个rejected的Promise阻止表单提交
      return Promise.reject(new Error(duplicateError));
    }
    if (!selectedCodes.length) {
      return Promise.reject(new Error('Select at least one IB link distribution.'));
    }

    // 将partnershipConditions重命名为partnership_conditions_multiple，符合后端要求
    const { partnershipConditions, ...restValues } = values;
    const payload = {
      ...restValues,
      user: userId,
      partnership_conditions_multiple: partnershipConditions,
      sales_codes: selectedCodes,
    };

    return createUserPartnerAccountRequest(payload)
      .then(response => {
        showFormSuccessMessage('partnershipLinkDistributionSuccess');
        return response;
      })
      .catch(requestError => {
        showFormFailMessage(requestError.response?.data?.detail || 'formFailMessageText');
        throw requestError;
      });
  });

  const handleConfirm = event => {
    if (!hasIbContractSignature && !showContractWarning && !invalid && selectedCodes.length) {
      if (event) event.preventDefault();
      setShowContractWarning(true);
      return;
    }

    return submit(event);
  };

  const REWARD_FREQUENCIES = {
    dailyInstant: intl.formatMessage({ id: 'justDailyInstant' }),
    monthlyInstant: intl.formatMessage({ id: 'justMonthlyInstant' }),
    monthlyDelayed: intl.formatMessage({ id: 'justMonthlyDelayed' }),
  };

  return (
    <form className="UserPartnerAccountCreateForm" onSubmit={handleConfirm}>
      <div className="UserPartnerAccountCreateForm__inputs-inner">
        <div className="UserPartnerAccountCreateForm__field">
          <label className="UserPartnerAccountCreateForm__field-label" htmlFor="partnershipConditions">
            <span>*</span>
            <FormattedMessage id="rewardTemplate" />
          </label>
          <div className="UserPartnerAccountCreateForm__field-control">
            <Field
              component={ReduxSelectField}
              name="partnershipConditions"
              maxMenuHeight={100}
              noLabel
              placeholder="Please select"
              options={partnershipConditionsList.map(cond => ({
                value: cond.id,
                label: cond.title,
              }))}
              validate={[required()]}
              disabled={submitting}
              searchable
              multi
              onMenuOpen={() => {
                // 每次打开下拉菜单时获取最新的数据
                if (getPartnershipConditionsList && userId) {
                  getPartnershipConditionsList(userId);
                }
              }}
            />
          </div>
        </div>

        <div className="UserPartnerAccountCreateForm__field">
          <label className="UserPartnerAccountCreateForm__field-label" htmlFor="frequencyOfPayments">
            <span>*</span>
            <FormattedMessage id="justFrequencyOfReward" />
          </label>
          <div className="UserPartnerAccountCreateForm__field-control">
            <Field
              component={ReduxSelectField}
              name="frequencyOfPayments"
              maxMenuHeight={100}
              noLabel
              placeholder="Please select"
              options={rewardFrequencies.filter(Boolean).map(freq => ({
                value: freq,
                label: REWARD_FREQUENCIES[freq] ?? '',
              }))}
              validate={[required()]}
              disabled={submitting}
            />
          </div>
        </div>

        <div className="UserPartnerAccountCreateForm__distribution">
          <h3>Select Link Distribution</h3>
          <LinkDistributionSelector
            userId={userId}
            resetKey={resetKey}
            onSelectionChange={setSelectedCodes}
            onError={handleDistributionError}
          />
          {showContractWarning && (
            <div className="UserPartnerAccountCreateForm__contract-warning">
              <FormattedMessage id="partnerAccountContractSignatureNotice" />
            </div>
          )}
          <div className="UserPartnerAccountCreateForm__selected">
            Selected: <strong>{selectedCodes.length}</strong>
          </div>
        </div>
      </div>

      <div className="UserPartnerAccountCreateForm__footer">
        <DefaultButton type="button" textId="justCancel" onClick={onRequestClose} disabled={submitting} />
        <DefaultButton
          textId="justConfirm"
          type="button"
          onClick={() => handleConfirm()}
          loading={submitting}
          disabled={invalid || !selectedCodes.length || submitting}
          filled
        />
      </div>
      {error && <strong className="formError">{error}</strong>}

      <style jsx>{staticStyles}</style>
    </form>
  );
};

UserPartnerAccountCreateForm.propTypes = {
  userId: PropTypes.number.isRequired,
  rewardFrequencies: PropTypes.array.isRequired,
  partnershipConditionsList: PropTypes.array.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  error: PropTypes.string,
  getPartnershipConditionsList: PropTypes.func,
  resetKey: PropTypes.number,
  showFormSuccessMessage: PropTypes.func,
  showFormFailMessage: PropTypes.func,
  onRequestClose: PropTypes.func,
  reset: PropTypes.func,
  hasIbContractSignature: PropTypes.bool,
};

UserPartnerAccountCreateForm.defaultProps = {
  error: '',
  getPartnershipConditionsList: null,
  resetKey: 0,
  onRequestClose: () => {},
  showFormSuccessMessage: () => {},
  showFormFailMessage: () => {},
  reset: () => {},
  hasIbContractSignature: false,
};

export default FormMessageHOC(
  reduxForm({
    form: 'user-partner-account-create-form',
    enableReinitialize: true,
    onSubmitSuccess: (result, dispatch, props) => {
      if (props.onSubmitSuccess) props.onSubmitSuccess(result, dispatch, props);
    },
  })(injectIntl(UserPartnerAccountCreateForm))
);
export { UserPartnerAccountCreateForm };
